import '../../resources/files.css';

import React, { SubmitEvent, use, useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Ratio from 'react-bootstrap/Ratio';
import Table from 'react-bootstrap/Table';
import { toast } from 'react-toastify';
import { FileModel } from '../../models/file';
import { AdminUserModel, AdminUserStatus } from '../../models/user';
import noThumbnail from '../../resources/nothumb.gif';
import { AppContext } from '../../utils/context';
import { formatDate, formatSize, formatSizeWithInfinite } from '../../utils/formatting';
import { useInputFieldSetter } from '../../utils/hooks';
import { errorMessage, logError, sortByDate } from '../../utils/misc';

const PAGE_SIZE = 25;

const STATUS_TABS: { status: AdminUserStatus; label: string }[] = [
    { status: 'pending', label: 'Not approved' },
    { status: 'approved', label: 'Approved' },
    { status: 'unverified', label: 'Unverified E-Mail' },
    { status: 'all', label: 'All' },
];

function isAdminUserStatus(value: string | null): value is AdminUserStatus {
    return STATUS_TABS.some((tab) => tab.status === value);
}

const UserStatusBadges: React.FC<{ readonly user: AdminUserModel }> = ({ user }) => {
    return (
        <>
            {user.admin ? (
                <Badge bg="info" className="me-1">
                    Admin
                </Badge>
            ) : null}
            {user.email_valid ? null : (
                <Badge bg="secondary" className="me-1">
                    Unverified
                </Badge>
            )}
            {user.approved ? (
                <Badge bg="success" className="me-1">
                    Approved
                </Badge>
            ) : (
                <Badge bg="warning" className="me-1">
                    Not approved
                </Badge>
            )}
        </>
    );
};

const UserRow: React.FC<{
    readonly user: AdminUserModel;
    readonly isSelf: boolean;
    readonly onApprove: (user: AdminUserModel) => void;
    readonly onBan: (user: AdminUserModel) => void;
    readonly onInspectFiles: (user: AdminUserModel) => void;
}> = ({ user, isSelf, onApprove, onBan, onInspectFiles }) => {
    const handleApprove = useCallback(() => {
        onApprove(user);
    }, [user, onApprove]);

    const handleBan = useCallback(() => {
        onBan(user);
    }, [user, onBan]);

    const handleInspectFiles = useCallback(() => {
        onInspectFiles(user);
    }, [user, onInspectFiles]);

    return (
        <tr>
            <td>{user.username}</td>
            <td>{user.email}</td>
            <td>
                <UserStatusBadges user={user} />
            </td>
            <td>{formatDate(user.created_at)}</td>
            <td>{`${formatSize(user.storage_used)} / ${formatSizeWithInfinite(user.storage_quota)}`}</td>
            <td className="text-nowrap">
                {user.approved ? null : (
                    <Button className="me-1" onClick={handleApprove} size="sm" variant="success">
                        Approve
                    </Button>
                )}
                {!isSelf && user.approved && !user.admin ? (
                    <Button className="me-1" onClick={handleBan} size="sm" variant="danger">
                        Ban
                    </Button>
                ) : null}
                <Button onClick={handleInspectFiles} size="sm" variant="secondary">
                    Inspect uploads
                </Button>
            </td>
        </tr>
    );
};

const UserFilesList: React.FC<{ readonly user: AdminUserModel }> = ({ user }) => {
    const { apiAccessor } = use(AppContext);
    const [files, setFiles] = useState<FileModel[] | undefined>(undefined);
    const [loadError, setLoadError] = useState<string | undefined>(undefined);

    useEffect(() => {
        const controller = new AbortController();
        void (async () => {
            try {
                const userFiles = await FileModel.getByUser(user, apiAccessor);
                if (!controller.signal.aborted) {
                    // eslint-disable-next-line unicorn/no-array-sort
                    setFiles(userFiles.sort(sortByDate));
                }
            } catch (error: unknown) {
                logError(error);
                if (!controller.signal.aborted) {
                    setLoadError(errorMessage(error));
                }
            }
        })();

        return () => {
            controller.abort();
        };
    }, [user, apiAccessor]);

    if (loadError) {
        return <p className="text-danger">Error loading uploads: {loadError}</p>;
    }

    if (!files) {
        return <p>Loading...</p>;
    }

    if (files.length === 0) {
        return <p>This user has no uploads.</p>;
    }

    return (
        <>
            <p>
                {files.length} uploads, {formatSize(user.storage_used)} total
            </p>
            <Row className="justify-content-center">
                {files.map((file) => {
                    return (
                        <Col className="col-auto mb-3" key={file.id}>
                            <Card bg="primary" className="file-card" text="white">
                                <Card.Header title={file.name}>{file.name}</Card.Header>
                                <Card.Body>
                                    <Ratio aspectRatio="1x1">
                                        <a href={`/view/${file.id}`} rel="noreferrer" target="_blank">
                                            <Card.Img src={file.thumbnail_url ?? noThumbnail} variant="top" />
                                        </a>
                                    </Ratio>
                                </Card.Body>
                                <Card.Footer className="d-flex">
                                    <div className="flex-grow-1 p-1" title={formatDate(file.created_at)}>
                                        {file.getFormattedSize()}
                                    </div>
                                    <a className="btn btn-sm btn-secondary" href={file.download_url}>
                                        Download
                                    </a>
                                </Card.Footer>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        </>
    );
};

const UserFilesModal: React.FC<{
    readonly user: AdminUserModel | undefined;
    readonly onHide: () => void;
}> = ({ user, onHide }) => {
    return (
        <Modal onHide={onHide} show={!!user} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Uploads of "{user?.username}"</Modal.Title>
            </Modal.Header>
            <Modal.Body>{user ? <UserFilesList key={user.id} user={user} /> : null}</Modal.Body>
            <Modal.Footer>
                <Button onClick={onHide} variant="secondary">
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const AdminUsersPage: React.FC = () => {
    const { apiAccessor, user: currentUser } = use(AppContext);
    const [status, setStatus] = useState<AdminUserStatus>('pending');
    const [searchInput, setSearchInput] = useInputFieldSetter('');
    const [search, setSearch] = useState('');
    const [offset, setOffset] = useState(0);
    const [reloadCounter, setReloadCounter] = useState(0);
    const [users, setUsers] = useState<AdminUserModel[] | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [userToBan, setUserToBan] = useState<AdminUserModel | undefined>(undefined);
    const [userToInspect, setUserToInspect] = useState<AdminUserModel | undefined>(undefined);

    useEffect(() => {
        const controller = new AbortController();
        void (async () => {
            try {
                const result = await AdminUserModel.list({ status, search, offset, limit: PAGE_SIZE }, apiAccessor);
                if (!controller.signal.aborted) {
                    setUsers(result.items);
                    setTotal(result.total);
                }
            } catch (error: unknown) {
                logError(error);
                toast.error(`Error loading users: ${errorMessage(error)}`);
            }
        })();

        return () => {
            controller.abort();
        };
    }, [status, search, offset, reloadCounter, apiAccessor]);

    const reload = useCallback(() => {
        setReloadCounter((counter) => counter + 1);
    }, []);

    const handleSelectStatus = useCallback((key: string | null) => {
        if (!isAdminUserStatus(key)) {
            return;
        }

        setStatus(key);
        setOffset(0);
    }, []);

    const handleSearch = useCallback(
        (event: SubmitEvent<HTMLFormElement>) => {
            event.preventDefault();
            setSearch(searchInput.trim());
            setOffset(0);
        },
        [searchInput],
    );

    const handlePreviousPage = useCallback(() => {
        setOffset((current) => Math.max(0, current - PAGE_SIZE));
    }, []);

    const handleNextPage = useCallback(() => {
        setOffset((current) => current + PAGE_SIZE);
    }, []);

    const handleApprove = useCallback(
        (user: AdminUserModel) => {
            void (async () => {
                try {
                    await toast.promise(user.approve(apiAccessor), {
                        success: `Approved user "${user.username}"!`,
                        pending: `Approving user "${user.username}"...`,
                        error: {
                            render({ data }) {
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                                const error = data as Error;
                                return `Error approving user "${user.username}": ${error.message}`;
                            },
                        },
                    });
                } catch (error: unknown) {
                    logError(error);
                }

                reload();
            })();
        },
        [apiAccessor, reload],
    );

    const handleBan = useCallback(() => {
        if (!userToBan) {
            return;
        }

        void (async () => {
            try {
                await toast.promise(userToBan.revokeApproval(apiAccessor), {
                    success: `Banned user "${userToBan.username}"!`,
                    pending: `Banning user "${userToBan.username}"...`,
                    error: {
                        render({ data }) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                            const error = data as Error;
                            return `Error banning user "${userToBan.username}": ${error.message}`;
                        },
                    },
                });
            } catch (error: unknown) {
                logError(error);
            } finally {
                setUserToBan(undefined);
            }

            reload();
        })();
    }, [userToBan, apiAccessor, reload]);

    const unsetUserToBan = useCallback(() => {
        setUserToBan(undefined);
    }, []);

    const unsetUserToInspect = useCallback(() => {
        setUserToInspect(undefined);
    }, []);

    let content: React.ReactNode = <h3>Loading...</h3>;
    if (users?.length === 0) {
        content = <p className="mt-3">No users found.</p>;
    } else if (users) {
        content = (
            <>
                <Table className="mt-3" hover responsive striped>
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>E-Mail</th>
                            <th>Status</th>
                            <th>Registered</th>
                            <th>Storage</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => {
                            return (
                                <UserRow
                                    isSelf={user.id === currentUser?.id}
                                    key={user.id}
                                    onApprove={handleApprove}
                                    onBan={setUserToBan}
                                    onInspectFiles={setUserToInspect}
                                    user={user}
                                />
                            );
                        })}
                    </tbody>
                </Table>
                <div className="d-flex align-items-center">
                    <Button disabled={offset <= 0} onClick={handlePreviousPage} variant="secondary">
                        Previous
                    </Button>
                    <span className="flex-grow-1 text-center">
                        Showing {offset + 1}-{offset + users.length} of {total}
                    </span>
                    <Button disabled={offset + PAGE_SIZE >= total} onClick={handleNextPage} variant="secondary">
                        Next
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            <Modal onHide={unsetUserToBan} show={!!userToBan}>
                <Modal.Header closeButton>
                    <Modal.Title>Ban user?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure to ban the user "{userToBan?.username}"?</p>
                    <p>
                        This revokes their approval. They will no longer be able to upload or edit files, or create
                        links. Their existing files and links stay online. You can approve them again later.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={unsetUserToBan} variant="secondary">
                        No
                    </Button>
                    <Button onClick={handleBan} variant="danger">
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <UserFilesModal onHide={unsetUserToInspect} user={userToInspect} />
            <h1>
                Manage users
                <span className="p-3" />
                <Button onClick={reload} variant="secondary">
                    Refresh
                </Button>
            </h1>
            <Nav activeKey={status} className="mt-3" onSelect={handleSelectStatus} role="tablist" variant="tabs">
                {STATUS_TABS.map((tab) => {
                    return (
                        <Nav.Item key={tab.status}>
                            <Nav.Link eventKey={tab.status}>{tab.label}</Nav.Link>
                        </Nav.Item>
                    );
                })}
            </Nav>
            <Form className="mt-3" onSubmit={handleSearch}>
                <InputGroup>
                    <Form.Control
                        aria-label="Search users"
                        name="search"
                        onChange={setSearchInput}
                        placeholder="Search by username or E-Mail"
                        type="text"
                        value={searchInput}
                    />
                    <Button type="submit" variant="primary">
                        Search
                    </Button>
                </InputGroup>
            </Form>
            {content}
        </>
    );
};
